import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { ORDERS_PAGE_SIZE, ORDERS_POLL_INTERVAL_MS } from "../constants/cryptoSwaps.constants";
import type { OtcPage } from "../types/cryptoSwaps.types";
import { clampPage, hasActiveFilters, type OtcHistoryFilters } from "../utils/cryptoSwapFilters";
import { cryptoSwapsAccessLatch, cryptoSwapsPrincipalKey } from "./cryptoSwapsAccessLatch";

export interface UseOtcOrdersPageResult<R, F extends string> {
  rows: R[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  filters: OtcHistoryFilters<F>;
  setFilter: <K extends keyof OtcHistoryFilters<F>>(key: K, value: OtcHistoryFilters<F>[K]) => void;
  setRange: (from: string, to: string) => void;
  clearFilters: () => void;
  isFiltered: boolean;
  /** First load only. A poll or a filter change never shows the skeleton over live rows. */
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  hasAccess: boolean;
  refresh: () => void;
}

/** `list` and `buildQuery` are dependencies: pass module-level functions. */
export interface UseOtcOrdersPageOptions<R, F extends string, Q> {
  list: (query: Q, signal: AbortSignal) => Promise<OtcPage<R>>;
  buildQuery: (filters: OtcHistoryFilters<F>, page: number) => Q;
  isLive: (row: R) => boolean;
  defaultFilters: OtcHistoryFilters<F>;
}

/** Server-side filtered history. Polls only while a visible row can still move. */
export function useOtcOrdersPage<R, F extends string, Q>({
  list,
  buildQuery,
  isLive,
  defaultFilters,
}: UseOtcOrdersPageOptions<R, F, Q>): UseOtcOrdersPageResult<R, F> {
  const { user } = useAuth();
  const principal = cryptoSwapsPrincipalKey(user?.id, user?.organization_id ?? user?.organization?.id);
  const hasAccess = !cryptoSwapsAccessLatch.useDenied(principal);

  const [filters, setFilters] = useState<OtcHistoryFilters<F>>(defaultFilters);
  const [page, setPageState] = useState(1);
  const [rows, setRows] = useState<R[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [reloadToken, requestReload] = useReducer((token: number) => token + 1, 0);

  const requestSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const hasLoadedOnceRef = useRef(false);

  const query = useMemo(() => buildQuery(filters, page), [buildQuery, filters, page]);

  const load = useCallback(
    async (background: boolean) => {
      if (cryptoSwapsAccessLatch.isDenied(principal)) return;
      const seq = ++requestSeqRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (background || hasLoadedOnceRef.current) setIsRefreshing(true);
      else setIsLoading(true);

      try {
        const result = await list(query, controller.signal);
        if (seq !== requestSeqRef.current) return;
        setRows(result.data);
        setTotal(result.total);
        setHasError(false);
      } catch (error: unknown) {
        if (seq !== requestSeqRef.current || controller.signal.aborted) return;
        if (HttpApiService.isAccessDeniedError(error)) {
          cryptoSwapsAccessLatch.latch(principal);
          return;
        }
        setHasError(true);
      } finally {
        if (seq === requestSeqRef.current) {
          hasLoadedOnceRef.current = true;
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [principal, query, list],
  );

  useEffect(() => {
    if (!user) return;
    void load(false);
  }, [user, load, reloadToken]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const hasLiveRow = rows.some(isLive);

  useEffect(() => {
    if (!hasLiveRow || !hasAccess) return;
    const intervalId = window.setInterval(() => void load(true), ORDERS_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [hasLiveRow, hasAccess, load]);

  const setPage = useCallback((next: number) => setPageState(clampPage(next, total)), [total]);

  const setFilter = useCallback(
    <K extends keyof OtcHistoryFilters<F>>(key: K, value: OtcHistoryFilters<F>[K]) => {
      setFilters((current) => (current[key] === value ? current : { ...current, [key]: value }));
      setPageState(1);
    },
    [],
  );

  const setRange = useCallback((from: string, to: string) => {
    setFilters((current) =>
      current.from === from && current.to === to ? current : { ...current, from, to },
    );
    setPageState(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
    setPageState(1);
  }, [defaultFilters]);

  return useMemo(
    () => ({
      rows,
      total,
      page,
      pageSize: ORDERS_PAGE_SIZE,
      setPage,
      filters,
      setFilter,
      setRange,
      clearFilters,
      isFiltered: hasActiveFilters(filters),
      isLoading,
      isRefreshing,
      hasError,
      hasAccess,
      refresh: requestReload,
    }),
    [
      rows,
      total,
      page,
      setPage,
      filters,
      setFilter,
      setRange,
      clearFilters,
      isLoading,
      isRefreshing,
      hasError,
      hasAccess,
    ],
  );
}
